import { useEffect, useState } from "react";
import { faTableColumns } from "@fortawesome/free-solid-svg-icons/faTableColumns";
import Divider from "@mui/material/Divider";
import FormGroup from "@mui/material/FormGroup";
import Box from "@mui/system/Box";
import GetIcon from "../font-awesome-icon";
import UIPopper from "../popover";
import TruncateTooltip from "../truncate-with-tooltip";
import UIButton from "../ui-controls/button";
import UICheckBox from "../ui-controls/check-box";
import { isCustomFieldColumnKey } from "@/services/custom-fields/fieldKeys";

const cloneColumns = (source = {}) => JSON.parse(JSON.stringify(source || {}));

const columnKeysInDisplayOrder = (
  source = {},
  defaultColumns = [],
  customFields = []
) => {
  const keys = Object.keys(source || {});
  const leadKeys = keys.filter(
    (key) => !isCustomFieldColumnKey(key, customFields)
  );
  const customKeys = keys.filter((key) =>
    isCustomFieldColumnKey(key, customFields)
  );
  const used = new Set();
  const ordered = [];

  (defaultColumns || []).forEach((name) => {
    const key = leadKeys.find(
      (k) => k.toLowerCase() === String(name).toLowerCase()
    );
    if (key && !used.has(key)) {
      ordered.push(key);
      used.add(key);
    }
  });

  leadKeys.forEach((key) => {
    if (!used.has(key)) ordered.push(key);
  });
  customKeys.forEach((key) => ordered.push(key));

  return ordered;
};

const orderColumnsByDefault = (
  source = {},
  defaultColumns = [],
  customFields = []
) => {
  const ordered = {};
  columnKeysInDisplayOrder(source, defaultColumns, customFields).forEach(
    (key) => {
      ordered[key] = source[key];
    }
  );
  return ordered;
};

const ManageColPopover = ({
  setColumns,
  columns,
  disabledColumns,
  defaultColumns,
  customFields = [],
}) => {
  const [cols, setCols] = useState(() =>
    orderColumnsByDefault(cloneColumns(columns), defaultColumns, customFields)
  );

  const syncFromProps = () => {
    setCols(
      orderColumnsByDefault(cloneColumns(columns), defaultColumns, customFields)
    );
  };

  const columnKeys = Object.keys(columns || {})
    .sort()
    .join("|");
  useEffect(() => {
    syncFromProps();
  }, [columnKeys]);

  const handleChange = (e, key) => {
    const temp = {
      ...cols,
      [key]: { ...cols[key], visible: e.target.checked },
    };
    setCols(temp);
  };

  const setAllVisible = (visible) => {
    const temp = cloneColumns(cols);
    Object.keys(temp).forEach((c) => {
      temp[c].visible = disabledColumns.includes(c) ? true : visible;
    });
    setCols(temp);
  };

  const getResetCols = () => {
    const temp = { ...cols };
    Object.keys(cols).forEach((c) => {
      temp[c].visible = defaultColumns.includes(c);
    });
    return temp;
  };

  const allVisible = Object.keys(cols).every((key) => cols[key].visible);
  const someVisible = Object.keys(cols).some((key) => cols[key].visible);

  return (
    <UIPopper
      onClose={syncFromProps}
      style={{ p: 1, width: "200px" }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      clickNode={
        <UIButton
          key={1}
          className="tour-manage-col-lead-btn"
          title={
            <GetIcon
              title="Manage Columns"
              icon={faTableColumns}
              iconStyles={{ marginRight: 8 }}
            />
          }
          variant={"outlined"}
        />
      }
    >
      {({ setAnchorEl }) => (
        <>
          <UICheckBox
            outlined={true}
            label={"Select All"}
            indeterminate={someVisible && !allVisible}
            checked={allVisible}
            handleChange={(e) => setAllVisible(e.target.checked)}
          />

          <Divider sx={{ my: "2" }} />
          <FormGroup
            className="custom-scroll"
            sx={{ display: "grid", height: "260px", overflowY: "scroll" }}
          >
            {columnKeysInDisplayOrder(cols, defaultColumns, customFields).map(
              (key, index) => (
                <UICheckBox
                  outlined={true}
                  disabled={disabledColumns.includes(key)}
                  sx={{ ml: "0px", mr: "2px" }}
                  key={index + 1}
                  checked={cols[key].visible}
                  label={
                    <TruncateTooltip
                      text={cols[key].header}
                      limit={20}
                      position="right"
                    />
                  }
                  handleChange={(e) => handleChange(e, key)}
                />
              )
            )}
          </FormGroup>
          <Box sx={{ mt: 1 }}>
            <UIButton
              fullWidth={true}
              size="small"
              sx={{ height: "37px" }}
              title={"Apply"}
              onClick={() => {
                setColumns(cols);
                setAnchorEl(null);
              }}
            />
          </Box>
          <Box sx={{ mt: 1 }}>
            <UIButton
              variant="outlined"
              fullWidth={true}
              size="small"
              sx={{ height: "37px" }}
              title={"Reset"}
              onClick={() => {
                setColumns(getResetCols());
                setAnchorEl(null);
              }}
            />
          </Box>
        </>
      )}
    </UIPopper>
  );
};

export default ManageColPopover;
